/** "Download PDF": fetch the image attachments' previews (as `data:` URIs —
 *  the CSP lets react-pdf neither fetch S3 nor `blob:` URLs), then load the
 *  renderer chunk and lay the thread out. An image that cannot be fetched
 *  (expired, deleted) is listed by name instead of embedded. */
import { fetchAttachmentBytes } from '@/api/diagnoseAttachments';

import type { ExportMeta, ExportTurn } from '../threadMarkdown';

/** PNG / JPEG from the magic bytes — the preview rendition is always one of
 *  the two, and react-pdf embeds nothing else. */
export function sniffImageMime(bytes: Uint8Array): 'image/png' | 'image/jpeg' | null {
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'image/png';
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return 'image/jpeg';
  return null;
}

export function toBase64(bytes: Uint8Array): string {
  let bin = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

/** Previews fetched at a time — a thread can carry 30 images of up to ~3.5 MB. */
export const IMAGE_FETCH_CONCURRENCY = 4;

async function loadImages(turns: ExportTurn[]): Promise<Record<string, string>> {
  const wanted = turns.flatMap((t) =>
    t.attachments.filter((a) => a.kind === 'image').map((a) => ({ taskId: t.taskId, id: a.id })),
  );
  const out: Record<string, string> = {};
  let next = 0;
  const worker = async () => {
    for (let item = wanted[next++]; item; item = wanted[next++]) {
      const { taskId, id } = item;
      try {
        const bytes = new Uint8Array(await fetchAttachmentBytes(taskId, id, 'preview'));
        const mime = sniffImageMime(bytes);
        if (mime) out[id] = `data:${mime};base64,${toBase64(bytes)}`;
      } catch {
        // Expired or unreadable: the file is listed by name instead.
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(IMAGE_FETCH_CONCURRENCY, wanted.length) }, worker));
  return out;
}

export async function buildThreadPdf(meta: ExportMeta, turns: ExportTurn[], origin: string): Promise<Blob> {
  const [images, { renderThreadPdf }] = await Promise.all([loadImages(turns), import('./renderThreadPdf')]);
  return renderThreadPdf({ meta, turns, images, origin });
}
